import { create } from 'zustand';
import {
  ticketService,
  type TicketWithConversation,
  type UpdateTicketDto,
  type CreateMessageDto,
  type SendWithUndoResult,
} from '@services/index';
import type { Message, Conversation } from '@centraoneplus/shared-types';
import { queryClient } from '@/lib/queryClient';
import { analyticsKeys } from '@hooks/useAnalytics';

/**
 * True when a send failed without telling us the server's verdict: the request
 * was aborted client-side (our own axios `timeout`), the connection dropped, or
 * the BFF gave up waiting on the ticket-service (it maps a no-response upstream
 * to 503). In all of these the reply may already be persisted, so treating it as
 * a plain failure invites a retry that creates a duplicate message — and, on the
 * email channel, a duplicate outbound email.
 */
function isIndeterminateSendError(error: unknown): boolean {
  const err = error as {
    code?: string;
    message?: string;
    response?: { status?: number };
  };
  const status = err?.response?.status;
  if (status === 408 || status === 502 || status === 503 || status === 504) {
    return true;
  }
  if (
    err?.code === 'ECONNABORTED' ||
    err?.code === 'ETIMEDOUT' ||
    err?.code === 'ERR_NETWORK'
  ) {
    return true;
  }
  // No response at all + a timeout-shaped message (axios' abort path).
  return !err?.response && /timeout|aborted|network/i.test(err?.message || '');
}

/** Does this message look like the reply we just tried to send? */
function matchesSentReply(message: Message, dto: CreateMessageDto): boolean {
  return (
    message.senderType !== 'customer' &&
    (message.isInternal ?? false) === (dto.isInternal ?? false) &&
    message.content === dto.content
  );
}

interface TicketState {
  // Data
  ticket: TicketWithConversation | null;
  conversation: Conversation | null;
  messages: Message[];

  // Loading states
  isLoading: boolean;
  isSending: boolean;
  isUpdating: boolean;
  error: string | null;
  // True when the ticket fetch came back 404 — the ticket was deleted or never
  // existed (e.g. a stale link from a notification). Distinct from a generic
  // fetch error so the UI can show a friendly "not found" state instead of a
  // raw "Request failed with status code 404".
  notFound: boolean;
  // True when the ticket fetch came back 403 — the ticket exists but is outside
  // the caller's scope (e.g. another agent's ticket opened from a copied link).
  accessDenied: boolean;

  // Actions
  fetchTicket: (id: string) => Promise<void>;
  fetchMessages: () => Promise<void>;
  sendMessage: (dto: CreateMessageDto) => Promise<void>;
  /**
   * Send an email reply with an undo window. Resolves with the held operation
   * (`mode: 'scheduled'`) or, when the reply went out immediately, the saved
   * message (`mode: 'sent'`, already appended to `messages`).
   */
  sendMessageWithUndo: (dto: CreateMessageDto & { operationId: string }) => Promise<SendWithUndoResult>;
  editMessage: (messageId: string, content: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  updateTicket: (dto: UpdateTicketDto) => Promise<void>;
  assignTicket: (assigneeId: string | null) => Promise<void>;
  addSecondaryAssignees: (userIds: string[]) => Promise<void>;
  removeSecondaryAssignee: (userId: string) => Promise<void>;
  updateStatus: (status: string) => Promise<void>;
  updatePriority: (priority: string) => Promise<void>;
  moveToStage: (stageId: string) => Promise<void>;
  changePipeline: (pipelineId: string) => Promise<void>;
  addTags: (tags: string[]) => Promise<void>;
  removeTags: (tags: string[]) => Promise<void>;
  addMessage: (message: Message) => void;
  reset: () => void;
}

export const useTicketStore = create<TicketState>()((set, get) => ({
  // Initial state
  ticket: null,
  conversation: null,
  messages: [],
  isLoading: false,
  isSending: false,
  isUpdating: false,
  error: null,
  notFound: false,
  accessDenied: false,

  // Fetch ticket with conversation
  fetchTicket: async (id) => {
    set({ isLoading: true, error: null, notFound: false, accessDenied: false });

    try {
      const ticket = await ticketService.getTicket(id);

      // Backend returns 'conversations' (plural array), frontend expects 'conversation' (singular)
      // Find the first email conversation, or fall back to any conversation
      const conversations = (ticket as any).conversations as Conversation[] | undefined;
      const emailConversation = conversations?.find((c: any) => c.channel?.toLowerCase() === 'email');
      const primaryConversation = ticket.conversation || emailConversation || conversations?.[0] || null;

      set({
        ticket,
        conversation: primaryConversation,
        isLoading: false,
      });

      if (primaryConversation) {
        get().fetchMessages();
      }
    } catch (error) {
      const status = (error as any)?.response?.status;
      const isNotFound = status === 404;
      const isAccessDenied = status === 403;
      set({
        // A ticket the caller can't see must not linger from a previous load.
        ...(isAccessDenied ? { ticket: null, conversation: null, messages: [] } : {}),
        notFound: isNotFound,
        accessDenied: isAccessDenied,
        error: isNotFound
          ? 'This ticket no longer exists. It may have been deleted or the link is out of date.'
          : isAccessDenied
            ? "You don't have access to this ticket. It is assigned outside your queue."
            : error instanceof Error
            ? error.message
            : 'Failed to fetch ticket',
        isLoading: false,
      });
    }
  },

  // Fetch messages for current conversation
  fetchMessages: async () => {
    const { ticket, conversation } = get();
    if (!ticket || !conversation) return;

    try {
      const messages = await ticketService.getMessages(ticket.id, conversation.id);
      set({ messages });
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  },

  // Send a new message
  sendMessage: async (dto) => {
    const { ticket, conversation } = get();
    if (!ticket || !conversation) {
      set({ error: 'No active conversation' });
      return;
    }

    set({ isSending: true, error: null });

    // Remembered so an indeterminate failure below can tell a reply that landed
    // server-side from one that never arrived.
    const knownMessageIds = new Set(get().messages.map((m) => m.id));

    try {
      const message = await ticketService.sendMessage(ticket.id, conversation.id, dto);
      set((state) => ({
        messages: [...state.messages, message],
        isSending: false,
      }));
    } catch (error) {
      // We may have lost the response to a timeout while the reply was in fact
      // saved. Ask the server what actually happened before calling it a
      // failure: if the reply is there, report success so the composer clears
      // instead of tempting a duplicate send.
      if (isIndeterminateSendError(error)) {
        await get().fetchMessages(); // swallows its own errors
        const landed = get().messages.some(
          (m) => !knownMessageIds.has(m.id) && matchesSentReply(m, dto),
        );
        if (landed) {
          set({ isSending: false, error: null });
          return;
        }
      }

      set({
        error: error instanceof Error ? error.message : 'Failed to send message',
        isSending: false,
      });
      // Rethrow so the caller (ReplyEditor.onSend) can detect the failure and
      // avoid clearing the composer as if the reply had been sent.
      throw error;
    }
  },

  sendMessageWithUndo: async (dto) => {
    const { ticket, conversation } = get();
    if (!ticket || !conversation) {
      set({ error: 'No active conversation' });
      throw new Error('No active conversation');
    }

    set({ isSending: true, error: null });
    const attempt = () => ticketService.sendMessageWithUndo(ticket.id, conversation.id, dto);

    try {
      let result: SendWithUndoResult;
      try {
        result = await attempt();
      } catch (error) {
        // We lost the answer (timeout / dropped connection / upstream 503). The
        // operation id makes a retry safe: the server returns the operation it
        // already holds instead of scheduling a second email.
        if (!isIndeterminateSendError(error)) throw error;
        result = await attempt();
      }

      if (result.mode === 'sent' && result.message) {
        const sent = result.message;
        set((state) => ({
          messages: state.messages.some((m) => m.id === sent.id) ? state.messages : [...state.messages, sent],
        }));
      }
      set({ isSending: false });
      return result;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to send message',
        isSending: false,
      });
      throw error;
    }
  },

  // Edit a message's content (Notes tab pencil-edit)
  editMessage: async (messageId, content) => {
    const { ticket, conversation } = get();
    if (!ticket || !conversation) return;
    try {
      const updated = await ticketService.updateMessage(ticket.id, conversation.id, messageId, content);
      set((state) => ({
        messages: state.messages.map((m) => (m.id === messageId ? { ...m, ...updated } : m)),
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to edit message' });
      // Rethrow so the caller can surface the failure (toast) and keep the
      // inline editor open instead of clearing it as if the edit succeeded.
      throw error;
    }
  },
  // Delete a message (Notes tab delete)
  deleteMessage: async (messageId) => {
    const { ticket, conversation } = get();
    if (!ticket || !conversation) return;
    try {
      await ticketService.deleteMessage(ticket.id, conversation.id, messageId);
      set((state) => ({
        messages: state.messages.filter((m) => m.id !== messageId),
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete message' });
      // Rethrow so the caller can surface the failure (toast) instead of
      // optimistically removing the note as if the delete succeeded.
      throw error;
    }
  },
  // Update ticket properties
  updateTicket: async (dto) => {
    const { ticket } = get();
    if (!ticket) return;

    set({ isUpdating: true, error: null });

    try {
      const updated = await ticketService.updateTicket(ticket.id, dto);
      set((state) => ({
        ticket: state.ticket ? { ...state.ticket, ...updated } : null,
        isUpdating: false,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update ticket',
        isUpdating: false,
      });
    }
  },

  // Assign ticket
  assignTicket: async (assigneeId) => {
    const { ticket } = get();
    if (!ticket) return;

    set({ isUpdating: true, error: null });

    try {
      const updated = await ticketService.assignTicket(ticket.id, assigneeId);
      set((state) => ({
        ticket: state.ticket ? { ...state.ticket, ...updated } : null,
        isUpdating: false,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to assign ticket',
        isUpdating: false,
      });
    }
  },

  // Secondary agents. The endpoints return the agent list, not the ticket, so
  // re-fetch the ticket to pick up secondaryAssignees (and its updatedAt).
  addSecondaryAssignees: async (userIds) => {
    const { ticket } = get();
    if (!ticket || userIds.length === 0) return;

    set({ isUpdating: true, error: null });

    try {
      await ticketService.addSecondaryAssignees(ticket.id, userIds);
      const fresh = await ticketService.getTicket(ticket.id);
      set((state) => ({ ticket: state.ticket ? fresh : null, isUpdating: false }));
      queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to add secondary agents',
        isUpdating: false,
      });
    }
  },

  removeSecondaryAssignee: async (userId) => {
    const { ticket } = get();
    if (!ticket) return;

    set({ isUpdating: true, error: null });

    try {
      await ticketService.removeSecondaryAssignee(ticket.id, userId);
      const fresh = await ticketService.getTicket(ticket.id);
      set((state) => ({ ticket: state.ticket ? fresh : null, isUpdating: false }));
      queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to remove secondary agent',
        isUpdating: false,
      });
    }
  },

  // Update status
  updateStatus: async (status) => {
    const { ticket } = get();
    if (!ticket) return;

    set({ isUpdating: true, error: null });

    try {
      const updated = await ticketService.updateStatus(ticket.id, status);
      set((state) => {
        if (!state.ticket) return { isUpdating: false };
        let merged: any = { ...state.ticket, ...updated };
        // Status→Stage sync in the UI: mirror the backend, which auto-moves the
        // ticket to the first pipeline stage whose mappedStatus equals the new
        // status. Apply the same locally so the Stage dropdown updates
        // immediately without a refresh (and regardless of what the status
        // response carries).
        const stages = merged.pipeline?.stages as Array<any> | undefined;
        if (Array.isArray(stages) && stages.length > 0) {
          const target = [...stages]
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
            .find((s) => s.mappedStatus === status);
          if (target && target.id !== merged.stageId) {
            merged = { ...merged, stageId: target.id, stage: target };
          }
        }
        return { ticket: merged, isUpdating: false };
      });
      // Status changes move dashboard KPIs (Resolved, distributions) — drop the
      // cached analytics so Reports reflects it without the 5-minute staleTime wait.
      queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update status',
        isUpdating: false,
      });
    }
  },

  // Update priority
  updatePriority: async (priority) => {
    const { ticket } = get();
    if (!ticket) return;

    set({ isUpdating: true, error: null });

    try {
      const updated = await ticketService.updatePriority(ticket.id, priority);
      set((state) => ({
        ticket: state.ticket ? { ...state.ticket, ...updated } : null,
        isUpdating: false,
      }));
      // Priority changes alter the Priority Breakdown distribution — drop the
      // cached analytics so Reports reflects it without the 5-minute staleTime wait.
      queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update priority',
        isUpdating: false,
      });
    }
  },

  // Move ticket to a different pipeline stage. The backend validates the
  // transition (required fields / exit conditions), records stage history, and
  // syncs the ticket status to the target stage's mapped status — so the
  // returned ticket carries the fresh `stage` relation AND `status`, and a plain
  // spread refreshes both in the sidebar.
  moveToStage: async (stageId) => {
    const { ticket } = get();
    if (!ticket) return;

    const pipelineId = (ticket as any).pipelineId as string | undefined;
    if (!pipelineId) {
      set({ error: 'This ticket is not in a pipeline, so its stage cannot be changed.' });
      return;
    }

    set({ isUpdating: true, error: null });

    try {
      const updated = await ticketService.moveToStage(ticket.id, pipelineId, stageId);
      set((state) => ({
        ticket: state.ticket ? { ...state.ticket, ...updated } : null,
        isUpdating: false,
      }));
      // A stage move can change the status (stage→status sync) and shifts the
      // board/distribution KPIs — drop cached analytics so Reports stays in sync.
      queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
    } catch (error) {
      // Surface the backend's validation reason (missing required fields, failed
      // exit conditions) rather than a generic "request failed" message.
      const data = (error as any)?.response?.data;
      let message =
        data?.message ?? (error instanceof Error ? error.message : 'Failed to move ticket to stage');
      if (Array.isArray(data?.missingFields) && data.missingFields.length > 0) {
        message = `${message}: ${data.missingFields.join(', ')}`;
      }
      set({ error: message, isUpdating: false });
    }
  },

  // Move ticket to a different pipeline. The backend relocates the ticket to the
  // new pipeline's first stage (or keeps the current stage if it belongs to the
  // new pipeline) and syncs the status. The PATCH response only carries the
  // changed scalar columns, not the embedded `pipeline.stages` the sidebar reads
  // for its Stage selector — so re-fetch the ticket to refresh those relations.
  changePipeline: async (pipelineId) => {
    const { ticket } = get();
    if (!ticket) return;
    if ((ticket as any).pipelineId === pipelineId) return;

    set({ isUpdating: true, error: null });

    try {
      await ticketService.updateTicket(ticket.id, { pipelineId });
      const fresh = await ticketService.getTicket(ticket.id);
      set((state) => ({
        ticket: state.ticket ? fresh : null,
        isUpdating: false,
      }));
      // A pipeline change relocates the stage and can change the status, both of
      // which move board/distribution KPIs — drop cached analytics so Reports
      // stays in sync without waiting out the staleTime.
      queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to change pipeline',
        isUpdating: false,
      });
    }
  },

  // Add tags
  addTags: async (tags) => {
    const { ticket } = get();
    if (!ticket) return;

    set({ isUpdating: true, error: null });

    try {
      const updated = await ticketService.addTags(ticket.id, tags);
      set((state) => ({
        ticket: state.ticket ? { ...state.ticket, tags: updated.tags } : null,
        isUpdating: false,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to add tags',
        isUpdating: false,
      });
    }
  },

  // Remove tags
  removeTags: async (tags) => {
    const { ticket } = get();
    if (!ticket) return;

    set({ isUpdating: true, error: null });

    try {
      const updated = await ticketService.removeTags(ticket.id, tags);
      set((state) => ({
        ticket: state.ticket ? { ...state.ticket, tags: updated.tags } : null,
        isUpdating: false,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to remove tags',
        isUpdating: false,
      });
    }
  },

  // Add message (for real-time updates via WebSocket)
  addMessage: (message) => {
    set((state) => ({
      messages: [...state.messages, message],
    }));
  },

  // Reset state
  reset: () => {
    set({
      ticket: null,
      conversation: null,
      messages: [],
      isLoading: false,
      isSending: false,
      isUpdating: false,
      error: null,
      notFound: false,
      accessDenied: false,
    });
  },
}));
